// ============================================================================
// CANCUN100 — PDFPickups.jsx (P1 — Confirmación de reserva / pickups, cliente)
// ============================================================================
// data: { folio, fecha_creacion, cliente:{nombre,hotel,telefono,email},
//         representante:{nombre,telefono},
//         tours: [{ fecha, nombre, hora_pickup, punto_pickup, codigo }] }
// ============================================================================
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import './pdfFonts';
import { COLORS } from './pdfStyles';
import { LOGO_BASE64 } from './logoBase64';

const styles = StyleSheet.create({
  page: { paddingTop: 36, paddingBottom: 36, paddingHorizontal: 38, fontFamily: 'DM Sans', color: COLORS.textoOscuro, fontSize: 10 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  logo: { width: 165, height: 38, objectFit: 'contain' },
  folioBox: { backgroundColor: COLORS.rosaSuave, borderRadius: 6, paddingVertical: 10, paddingHorizontal: 16, minWidth: 150, alignItems: 'flex-start' },
  folioLabel: { fontSize: 7, color: COLORS.textoMedio, letterSpacing: 1.5, fontFamily: 'DM Sans', fontWeight: 500, marginBottom: 3 },
  folioNumber: { fontSize: 22, color: COLORS.rosa, fontFamily: 'Space Grotesk', fontWeight: 700 },
  folioDate: { fontSize: 9, color: COLORS.textoMedio, marginTop: 2 },
  titulo: { fontSize: 13, color: COLORS.rosa, fontFamily: 'DM Sans', fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 },
  divisor: { height: 1.5, backgroundColor: COLORS.rosa, marginVertical: 12 },
  clientGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 },
  clientItem: { width: '50%', paddingVertical: 5, paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: COLORS.rosa, marginBottom: 8 },
  clientLabel: { fontSize: 7, color: COLORS.textoClaro, letterSpacing: 1, marginBottom: 3, fontWeight: 500 },
  clientValue: { fontSize: 11, color: COLORS.textoOscuro, fontWeight: 700 },
  sectionTitle: { fontSize: 10, color: COLORS.rosa, fontWeight: 700, letterSpacing: 2, marginTop: 8, marginBottom: 8 },
  th: { flexDirection: 'row', backgroundColor: COLORS.rosaSuave, paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 2, borderBottomColor: COLORS.rosa },
  thCell: { fontSize: 7.5, color: COLORS.rosa, fontWeight: 700, letterSpacing: 0.8 },
  tr: { flexDirection: 'row', paddingVertical: 9, paddingHorizontal: 8, borderBottomWidth: 0.5, borderBottomColor: COLORS.bordeClaro },
  colFecha: { width: '16%' }, colTour: { width: '32%' }, colHora: { width: '16%' }, colPunto: { width: '24%' }, colCodigo: { width: '12%' },
  cellName: { fontSize: 9.5, color: COLORS.textoOscuro, fontWeight: 700 },
  cell: { fontSize: 9, color: COLORS.textoMedio },
  cellCodigo: { fontSize: 9.5, color: COLORS.rosa, fontWeight: 700, fontFamily: 'Space Grotesk' },
  recoBox: { marginTop: 18, borderWidth: 1, borderColor: COLORS.bordeClaro, borderRadius: 8, padding: 14, backgroundColor: COLORS.fondoFila },
  recoTitle: { fontSize: 9, color: COLORS.rosa, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8 },
  recoItem: { fontSize: 9.5, color: COLORS.textoMedio, marginBottom: 5, lineHeight: 1.4 },
  footer: { marginTop: 20, alignItems: 'center' },
  logoFooter: { width: 120, height: 28, objectFit: 'contain', marginBottom: 6 },
  footerText: { fontSize: 9, color: COLORS.textoMedio },
});

const PDFPickups = ({ data }) => {
  const tours = data?.tours || [];
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header}>
          <Image src={LOGO_BASE64} style={styles.logo} />
          <View style={styles.folioBox}>
            <Text style={styles.folioLabel}>CONFIRMACIÓN</Text>
            <Text style={styles.folioNumber}>{data?.folio || '—'}</Text>
            <Text style={styles.folioDate}>{data?.fecha_creacion || ''}</Text>
          </View>
        </View>

        <Text style={styles.titulo}>CONFIRMACIÓN DE RESERVA {data?.folio || ''}</Text>
        <View style={styles.divisor} />

        <View style={styles.clientGrid}>
          <View style={styles.clientItem}><Text style={styles.clientLabel}>CLIENTE</Text><Text style={styles.clientValue}>{data?.cliente?.nombre || '—'}</Text></View>
          <View style={styles.clientItem}><Text style={styles.clientLabel}>HOTEL</Text><Text style={styles.clientValue}>{data?.cliente?.hotel || '—'}</Text></View>
          {data?.cliente?.telefono ? <View style={styles.clientItem}><Text style={styles.clientLabel}>TELÉFONO</Text><Text style={styles.clientValue}>{data.cliente.telefono}</Text></View> : null}
          {data?.representante?.nombre ? <View style={styles.clientItem}><Text style={styles.clientLabel}>REPRESENTANTE</Text><Text style={styles.clientValue}>{data.representante.nombre}</Text></View> : null}
        </View>

        <Text style={styles.sectionTitle}>ITINERARIO Y PUNTOS DE ENCUENTRO</Text>
        <View style={styles.th}>
          <Text style={[styles.thCell, styles.colFecha]}>FECHA</Text>
          <Text style={[styles.thCell, styles.colTour]}>TOUR</Text>
          <Text style={[styles.thCell, styles.colHora]}>HORA DE PICKUP</Text>
          <Text style={[styles.thCell, styles.colPunto]}>PUNTO DE ENCUENTRO</Text>
          <Text style={[styles.thCell, styles.colCodigo]}>CÓDIGO</Text>
        </View>
        {tours.map((t, i) => (
          <View key={i} style={styles.tr} wrap={false}>
            <Text style={[styles.cell, styles.colFecha]}>{t.fecha || '—'}</Text>
            <Text style={[styles.cellName, styles.colTour]}>{t.nombre || '—'}</Text>
            <Text style={[styles.cell, styles.colHora]}>{t.hora_pickup || '—'}</Text>
            <Text style={[styles.cell, styles.colPunto]}>{t.punto_pickup || '—'}</Text>
            <Text style={[styles.cellCodigo, styles.colCodigo]}>{t.codigo || '—'}</Text>
          </View>
        ))}

        <View style={styles.recoBox}>
          <Text style={styles.recoTitle}>RECOMENDACIONES</Text>
          <Text style={styles.recoItem}>• Preséntate 10 minutos antes de tu hora de pickup en el punto indicado.</Text>
          <Text style={styles.recoItem}>• El transporte compartido no puede esperar a pasajeros demorados.</Text>
          <Text style={styles.recoItem}>• Ten a la mano esta confirmación y una identificación.</Text>
          <Text style={styles.recoItem}>• Cualquier cambio, contacta a tu representante.</Text>
        </View>

        <View style={styles.footer}>
          <Image src={LOGO_BASE64} style={styles.logoFooter} />
          <Text style={styles.footerText}>cancun100.com · ¡Gracias por confiar en nosotros! 🌴</Text>
        </View>
      </Page>
    </Document>
  );
};

export default PDFPickups;
